import { getAnalyticsReportTabsSx } from '../../../shared/report-design';
import { useState, useEffect, useMemo } from 'react';
import { Box, Tab, Typography, LinearProgress, Card, CardContent } from '@mui/material';
import ResponsiveTabs from '../../../../system/responsive-tabs';
import { resolveShowCost } from '../../../../system/report-visibility';
import { formatUsd } from '../../../../system/report-formatters';


const TABS = [
  { label: "Images", key: 0 },
  { label: "Headlines", key: 1 },
  { label: "AD Copy", key: 2 },
  { label: "Call to Action", key: 3 },
];

function CreativeDetailTabs({ rowData, appVars, sDate, eDate, reportProfile }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [detailData, setDetailData] = useState([[], [], [], []]);
  const [loading, setLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const showCost = resolveShowCost(reportProfile);

  const normalizeTextItems = (source, keys) => {
    const output = [];
    const push = (val, raw) => {
      if (val == null) return;
      if (Array.isArray(val)) {
        val.forEach((item) => push(item, raw));
        return;
      }
      if (typeof val === 'object') {
        const key = keys.find((k) => val[k] != null);
        if (key) {
          push(val[key], val);
          return;
        }
        if (val.text != null) return push(val.text, val);
        if (val.label != null) return push(val.label, val);
        if (val.value != null) return push(val.value, val);
        if (val.name != null) return push(val.name, val);
        return;
      }
      const text = String(val).trim();
      if (text) output.push({ text, raw });
    };

    if (source && typeof source === 'object' && !Array.isArray(source)) {
      const key = keys.find((k) => source[k] != null);
      if (key) {
        push(source[key], source);
        return output;
      }
    }

    push(source, source);
    return output;
  };

  const normalizeImageItems = (source) => {
    const output = [];
    const push = (val, raw) => {
      if (val == null) return;
      if (Array.isArray(val)) {
        val.forEach((item) => push(item, raw));
        return;
      }
      if (typeof val === 'object') {
        const url =
          val.IMAGE_ASSET_URL ||
          val.IMAGE_URL ||
          val.URL ||
          val.url ||
          val.image_url ||
          val.image;
        const name = val.NAME || val.name || val.filename || val.FILE_NAME || val.TITLE || val.title;
        if (url) {
          output.push({ url, name: name || url, raw: val });
          return;
        }
        const key = ['IMAGE_ASSET_URL', 'IMAGE_URL', 'URL', 'url', 'image_url', 'image'].find((k) => val[k] != null);
        if (key) {
          push(val[key]);
        }
        return;
      }
      const text = String(val).trim();
      if (text) output.push({ url: text, name: text, raw });
    };

    if (source && typeof source === 'object' && !Array.isArray(source)) {
      const key = ['IMAGE_ASSET_URL', 'IMAGE_URL', 'URL', 'url', 'image_url', 'image'].find((k) => source[k] != null);
      if (key) {
        push(source[key], source);
        return output;
      }
    }

    push(source, source);
    return output;
  };

  const imageItems = useMemo(() => normalizeImageItems(detailData[0]), [detailData]);
  const headlineItems = useMemo(() => normalizeTextItems(detailData[1], ['TITLE', 'HEADLINE', 'TEXT', 'NAME']), [detailData]);
  const adCopyItems = useMemo(() => normalizeTextItems(detailData[2], ['BODY_COPY', 'BODY', 'TEXT', 'COPY', 'MESSAGE', 'DESCRIPTION']), [detailData]);
  const ctaItems = useMemo(() => normalizeTextItems(detailData[3], ['CTA', 'CALL_TO_ACTION', 'ACTION', 'TEXT', 'TITLE', 'NAME']), [detailData]);

  const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
  const formatMoney = (value) => formatUsd(Number(value || 0));
  const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

  const pickMetric = (row, keys) => {
    if (!row || typeof row !== 'object') return null;
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null) return Number(row[key]);
    }
    const lowerKeys = keys.map((key) => String(key).toLowerCase());
    const rowKey = Object.keys(row).find((k) => lowerKeys.includes(String(k).toLowerCase()));
    if (rowKey) return Number(row[rowKey]);
    return null;
  };

  const getStats = (row) => {
    if (!row || typeof row !== 'object') return null;
    const impressions = pickMetric(row, ['IMPRESSIONS', 'impressions']);
    const clicks = pickMetric(row, ['CLICKS', 'clicks']);
    const conversions = pickMetric(row, ['CONVERSIONS', 'conversions']);
    const cost = pickMetric(row, ['COST', 'cost', 'SPEND', 'spend']);
    const revenue = pickMetric(row, ['REVENUE', 'revenue']);
    const ctrRaw = pickMetric(row, ['CTR', 'ctr']);
    const convRateRaw = pickMetric(row, ['CONV_RATE', 'CONVRATE', 'convrate', 'convRate']);
    const ctr = ctrRaw ?? (impressions && impressions > 0 ? (Number(clicks || 0) / impressions) * 100 : null);
    const convRate = convRateRaw ?? (clicks && clicks > 0 ? (Number(conversions || 0) / clicks) * 100 : null);
    const roas = cost && cost > 0 && revenue != null ? revenue / cost : null;
    const hasAny = [impressions, clicks, conversions, cost, revenue, ctr, convRate].some((val) => val !== null && val !== undefined);
    return { impressions, clicks, conversions, cost, revenue, ctr, convRate, roas, hasAny };
  };

  const summaryStats = getStats(rowData);
  const summaryItems = useMemo(() => {
    if (!summaryStats?.hasAny) return [];
    const items = [
      { label: 'Impr', value: summaryStats.impressions, format: formatNumber },
      { label: 'Clicks', value: summaryStats.clicks, format: formatNumber },
      { label: 'Conv', value: summaryStats.conversions, format: formatNumber },
      { label: 'CTR', value: summaryStats.ctr, format: formatPercent },
      { label: 'Conv%', value: summaryStats.convRate, format: formatPercent },
    ];
    if (showCost) {
      items.push(
        { label: 'Spend', value: summaryStats.cost, format: formatMoney },
        { label: 'ROAS', value: summaryStats.roas, format: (val) => (val != null ? `${Number(val).toFixed(2)}x` : '0.00x') }
      );
    }
    return items.filter((item) => item.value !== null && item.value !== undefined);
  }, [showCost, summaryStats]);



  useEffect(() => {
    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      "cid": sessionStorage.getItem('rid'),
      "adgroup": rowData.ID,
      "sdate": sessionStorage.getItem('sDate'),
      "edate": sessionStorage.getItem('eDate')
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };

    fetch(`${appVars.api_url}/meta/creatives/adgroup`, requestOptions)
      .then(res => res.json())
      .then(async creatives => {
        setDetailData([creatives[0], creatives[1], creatives[2], creatives[3] || []]);
        // Preload images for first tab (Images) — replicate Reporting Admin pattern

      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [rowData.ADGROUP, sDate, eDate, appVars.api_url]);

  return (
    <Box sx={{ p: 1, bgcolor: "background.default" }}>
      {summaryItems.length ? (
        <Card variant="outlined" sx={{ mb: 1.5 }}>
          <CardContent sx={{ display: 'grid', gap: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Ad Group Performance
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.5, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
              {summaryItems.map((item) => (
                <Box key={item.label} sx={{ display: 'grid', gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {item.format(item.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      ) : null}
      <ResponsiveTabs sx={getAnalyticsReportTabsSx()} value={tabIndex} onChange={(_, val) => setTabIndex(val)}>
        {TABS.map(tab => <Tab key={tab.key} label={tab.label} />)}
      </ResponsiveTabs>
      <Box sx={{ pt: 2 }}>
        {loading ? <LinearProgress /> : (
          <>
            {tabIndex === 0 && (
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                {imageItems.length ? imageItems.map((item, index) => (
                  <Card key={`${item.url}-${index}`} variant="outlined">
                    <CardContent sx={{ display: 'grid', gap: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 120,
                          borderRadius: 1,
                          border: '1px solid rgba(0,0,0,0.08)',
                          background: '#fff',
                          p: 0.5
                        }}
                      >
                        <img
                          style={{ maxWidth: '100%', maxHeight: 140, cursor: 'pointer' }}
                          alt={item.name}
                          src={item.url}
                          onClick={() => {
                            setLightboxImage(item.url);
                            setLightboxOpen(true);
                          }}
                          onError={e => {
                            e.target.src = 'https://api.digitalwerks.com/imageFiles/no-image-icon.png';
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" noWrap title={item.name}>
                        {item.name}
                      </Typography>
                    </CardContent>
                  </Card>
                )) : (
                  <Typography variant="body2" color="text.secondary">No images available.</Typography>
                )}
              </Box>
            )}
            {tabIndex === 1 && (
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                {headlineItems.length ? headlineItems.map((text, index) => (
                  <Card key={`${text.text}-${index}`} variant="outlined">
                    <CardContent sx={{ display: 'grid', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Headline {index + 1}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{text.text}</Typography>
                    </CardContent>
                  </Card>
                )) : (
                  <Typography variant="body2" color="text.secondary">No headlines available.</Typography>
                )}
              </Box>
            )}
            {tabIndex === 2 && (
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                {adCopyItems.length ? adCopyItems.map((text, index) => (
                  <Card key={`${text.text}-${index}`} variant="outlined">
                    <CardContent sx={{ display: 'grid', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Ad Copy {index + 1}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{text.text}</Typography>
                    </CardContent>
                  </Card>
                )) : (
                  <Typography variant="body2" color="text.secondary">No ad copy available.</Typography>
                )}
              </Box>
            )}
            {tabIndex === 3 && (
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                {ctaItems.length ? ctaItems.map((text, index) => (
                  <Card key={`${text.text}-${index}`} variant="outlined">
                    <CardContent sx={{ display: 'grid', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">CTA {index + 1}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{text.text}</Typography>
                    </CardContent>
                  </Card>
                )) : (
                  <Typography variant="body2" color="text.secondary">No calls to action available.</Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
      {lightboxOpen && (
        <Box
          onClick={() => setLightboxOpen(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'zoom-out'
          }}
        >
          <img
            src={lightboxImage}
            alt="Preview"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              boxShadow: '0 0 20px #000',
              borderRadius: 8,
            }}
          />
        </Box>
      )}
    </Box>








  );
}

export default CreativeDetailTabs;
